import {
  DestroyRef,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
  BrnCalendarI18n,
  injectBrnCalendarI18n,
  provideBrnCalendarI18n,
} from '@spartan-ng/brain/calendar';
import { provideNativeDateAdapter } from '@spartan-ng/brain/date-time';

const DEFAULT_LOCALE = 'pt-BR';

/** Nomes de mês/dia de um locale — calculados uma vez por locale. */
const cache = new Map<string, Partial<BrnCalendarI18n>>();

/**
 * Config de i18n do calendário spartan para um locale, derivada de `Intl`.
 *
 * O `defaultCalendarI18n` do brain localiza cabeçalho/mês/ano via
 * `toLocaleDateString`, mas traz nomes de dias e meses fixos em inglês e semana
 * começando no domingo. Aqui a semana começa na segunda, como no `weekStartsOn`
 * que o PrimeNG usava. (2024-01-07 é um domingo — base para os nomes dos dias.)
 */
export function calendarI18nFor(
  lang?: string | null,
): Partial<BrnCalendarI18n> {
  const locale = lang || DEFAULT_LOCALE;
  const cached = cache.get(locale);
  if (cached) return cached;

  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const monthFmt = new Intl.DateTimeFormat(locale, { month: 'long' });
  const narrowFmt = new Intl.DateTimeFormat(locale, { weekday: 'narrow' });
  const longFmt = new Intl.DateTimeFormat(locale, { weekday: 'long' });

  const months = Array.from({ length: 12 }, (_, m) =>
    cap(monthFmt.format(new Date(2024, m, 1))),
  ) as ReturnType<BrnCalendarI18n['months']>;
  const narrow = Array.from({ length: 7 }, (_, i) =>
    cap(narrowFmt.format(new Date(2024, 0, 7 + i))),
  );
  const long = Array.from({ length: 7 }, (_, i) =>
    cap(longFmt.format(new Date(2024, 0, 7 + i))),
  );

  const config: Partial<BrnCalendarI18n> = {
    months: () => months,
    formatMonth: (month) => months[month],
    formatYear: (year) => `${year}`,
    formatHeader: (month, year) => `${months[month]} ${year}`,
    formatWeekdayName: (index) => narrow[index],
    labelWeekday: (index) => long[index],
    firstDayOfWeek: () => 1,
  };

  cache.set(locale, config);
  return config;
}

/**
 * Calendários spartan do app: adapter de data, config inicial e sincronização
 * com o idioma corrente — `use()` é a forma documentada pelo spartan de trocar
 * a config em runtime.
 */
export function provideCalendarI18n(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideNativeDateAdapter(),
    provideBrnCalendarI18n(calendarI18nFor(DEFAULT_LOCALE)),
    provideAppInitializer(() => {
      const calendarI18n = injectBrnCalendarI18n();
      const destroyRef = inject(DestroyRef);
      inject(TranslateService)
        .onLangChange.pipe(takeUntilDestroyed(destroyRef))
        .subscribe(({ lang }) => calendarI18n.use(calendarI18nFor(lang)));
    }),
  ]);
}
